import { Component, ChangeDetectionStrategy } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { getMyApp } from 'app/app-interface';
import { isCapacitorNative, isPlatformIOS } from 'environments/environment-helpers';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { CsPage, NavController } from 'providers/nav-controller';
import { MenuController } from 'providers/menu-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { LoggingService } from 'services/logging.service';
import { FatalErrorReport } from 'services/fatal-error-report';
import { FatalErrorKind } from './fatal-error-interface';

/** What one kind of fatal error screen shows. The texts are functions so the translation
 *  is read when the page is built, in the language active then. */
interface FatalErrorScreen {
  title: () => string;
  message: () => string;
  help: () => string;
  showRetry: boolean;
  showLogout: boolean;
  /** #4401: may this kind offer "start this device's data over"? It is shown only when
   *  the params also say the reset can help (FatalErrorPageParams.canResetLocalStore). */
  mayOfferLocalReset: boolean;
}

/**
 * Every kind's screen, in one place. A `Record` over the enum, so a kind added to
 * FatalErrorKind without a screen here fails the build instead of quietly showing the
 * generic "try again later" screen.
 */
const FATAL_ERROR_SCREENS: Record<FatalErrorKind, FatalErrorScreen> = {
  [FatalErrorKind.DatabaseRecoveryError]: {
    title: () => LL.fatalError.title.whoops(),
    message: () => LL.fatalError.messages.cantAccessData(),
    help: () => LL.fatalError.help.retryOrLogout(),
    showRetry: true,
    showLogout: true,
    mayOfferLocalReset: false,
  },
  [FatalErrorKind.TestFatalError]: {
    title: () => LL.fatalError.title.testError(),
    message: () => LL.fatalError.messages.testError(),
    help: () => LL.fatalError.help.testError(),
    showRetry: true,
    showLogout: false,
    mayOfferLocalReset: false,
  },
  [FatalErrorKind.BootingError]: {
    title: () => LL.fatalError.title.whoops(),
    message: () => LL.fatalError.messages.appFailedToStart(),
    help: () => LL.fatalError.help.retryOrRelogin(),
    showRetry: true,
    showLogout: true,
    mayOfferLocalReset: false,
  },
  [FatalErrorKind.LockdownModeError]: {
    title: () => LL.fatalError.title.whoops(),
    message: () => LL.fatalError.messages.lockdownMode(),
    help: () => LL.fatalError.help.disableLockdown(),
    showRetry: true,
    showLogout: false,
    mayOfferLocalReset: false,
  },
  [FatalErrorKind.LocalStorageWedged]: {
    // #4401: the device's own storage accepted the app's reads and answered none of
    // them, and reloading has already been tried as often as it is going to be. What
    // matters most here is that the advice is CORRECT for the platform: closing a tab
    // does not clear this on iOS, and telling somebody to do that sends them to do
    // something that cannot work.
    title: () => LL.fatalError.title.storageNotResponding(),
    message: () => LL.fatalError.messages.localStorageWedged(),
    help: () => isCapacitorNative()
      ? LL.fatalError.help.storageRestartApp()
      : (isPlatformIOS() ? LL.fatalError.help.storageForceQuitIos() : LL.fatalError.help.storageRestartBrowser()),
    showRetry: true,
    showLogout: false,
    mayOfferLocalReset: true,
  },
  [FatalErrorKind.ServerError]: {
    title: () => LL.fatalError.title.whoops(),
    message: () => LL.fatalError.messages.serverError(),
    help: () => LL.fatalError.help.tryAgainLater(),
    showRetry: true,
    showLogout: false,
    mayOfferLocalReset: false,
  },
};

@Component({
    selector: 'page-fatal-error',
    templateUrl: 'fatal-error.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: ModalPageMarker, useValue: new ModalPageMarker({ width: 600, height: 400 }) }]
})
export class FatalErrorPage extends CsPage<{}> {
  protected LL = LL;
  titleText = "Whoops...";
  messageText = '';
  errorHelpText = 'Please try again later.';
  showRetry = true;
  showLogout = false;
  /** #4401: offer to start this device's local copy over. Only for a storage jam the app
   *  can actually escape — see FatalErrorPageParams.canResetLocalStore. */
  showResetLocalData = false;
  /** #4401: the sentence explaining what starting over costs, shown beside that button. */
  resetLocalDataOfferText = '';

  constructor(
    public navCtrl: NavController,
    private menuCtrl: MenuController,
    private alertCtrl: AlertController,
    private logging: LoggingService,
    
  ) {
    super();
    // Set default texts with translation
    this.titleText = LL.fatalError.title.whoops();
    this.errorHelpText = LL.fatalError.help.tryAgainLater();

    // this happens when the app is reloaded into the fatal error page - we will redirect in ionViewWillEnter
    if (!getMyApp().fatalErrorParams) {
      return;
    }

    const params = getMyApp().fatalErrorParams;
    const screen: FatalErrorScreen | undefined = FATAL_ERROR_SCREENS[params.type];
    // if the kind is not one we know (an old reload, a value from outside the type), keep the
    // generic "try again later" screen set above rather than showing nothing
    if (!screen) {
      return;
    }
    this.titleText = screen.title();
    this.messageText = screen.message();
    this.errorHelpText = screen.help();
    this.showRetry = screen.showRetry;
    this.showLogout = screen.showLogout;
    // the reset offer depends on what the probe found, not only on the kind
    this.showResetLocalData = screen.mayOfferLocalReset && params.canResetLocalStore === true;
    if (this.showResetLocalData) {
      this.resetLocalDataOfferText = LL.fatalError.messages.resetLocalDataOffer();
    }
  }

  async ionViewCanEnter() {
    // (this happens when the app is reloaded into the fatal error page)
    if (!getMyApp().fatalErrorParams) {
      console.log('FatalErrorPage: no error params');
      // reload to see the current status (i.e. rechecks the error)
      await getMyApp().reloadAppAsync({ resetToRoot: true });
      return false;
    }
    return true;
  }

  async ionViewWillEnter() {
    this.menuCtrl.enable(false);
    this.logging.trackFatalErrorScreen(FatalErrorReport.Shown, getMyApp().fatalErrorParams);
  }

  async ionViewWillLeave() {
    this.menuCtrl.enable(true);
  }

  async retry() {
    await getMyApp().reloadAppAsync({ resetToRoot: true });
  }

  /**
   * Start this device's local copy over (#4401).
   *
   * Asks first, because it can cost the user anything this device had not managed to sync
   * yet — and the alert says so in those words rather than in ours.
   */
  async resetLocalData() {
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.fatalError.alerts.resetLocalDataTitle(),
      message: LL.fatalError.alerts.resetLocalDataMessage(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.fatalError.buttons.resetLocalData(), role: AlertButtonRole.Primary, result: true },
      ],
    });
    if (!confirmed) return;
    await getMyApp().resetLocalStoreAndReloadAsync();
  }

  async logout() {
    // note that we do NOT call app.logoutAsync() here, because we might not be logged in correctly yet! but we do want to make sure we get a clean slate
    await getMyApp().reloadAppAsync({ resetToRoot: true, clearLocalData: true });
  }
}
